import { Controller } from "@hotwired/stimulus";
import noUiSlider from "nouislider";

const getCurrencySymbol = (currencyCode) => {
  try {
    const formatted = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "symbol",
    }).format(1);
    return formatted.replace(/[\d.,\s]/g, "");
  } catch {
    return "$";
  }
};

export default class extends Controller {
  static targets = [
    "overlay",
    "drawer",
    "form",
    "amenities",
    "amenitiesInput",
    "propertyTypes",
    "starRating",
    "starRatingInput",
    "priceMinInput",
    "priceMaxInput",
    "currencySymbol",
    "rangeMinSlider",
    "rangeMaxSlider",
    "rangeSlider",
    "histogram",
    "priceRangeMinInput",
    "priceRangeMaxInput"
  ];

  connect() {
    this.setInitialCurrencySymbol();
    this.updateCurrencyAndSliderRanges();
    window.addEventListener("currencyChanged", this.handleCurrencyChangeEvent);
    this.updateMinMaxInputs();
    this.updateInputValues();
    // this.open();
  }

  disconnect() {
    window.removeEventListener(
      "currencyChanged",
      this.handleCurrencyChangeEvent,
    );
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
  }

  // --- Currency & Min/Max Handling ---

  // Always returns the base [usdMin, usdMax] from the hidden field (JSON)
  get basePriceRangeUSD() {
    try {
      return JSON.parse(`[${this.priceRangeMinInputTarget.value},${this.priceRangeMaxInputTarget.value}]` || "[0,999]");
    } catch {
      return [0, 999];
    }
  }

  updateInputValues() {
    const urlParam = new URLSearchParams(window.location.search);
    let minParam = urlParam.get("min");
    let maxParam = urlParam.get("max");
    if (!minParam || !maxParam) {
      return;
    }
    let min = this.convertToActiveCurrency(minParam);
    let max = this.convertToActiveCurrency(maxParam);

    if (!this.isDefaultPrice(`[${min},${max}]`)) {
      this.priceMinInputTarget.value = min;
      this.priceMaxInputTarget.value = max;
      this.renderHistogram();
      this.slider.set([min, max]);
    }
  }

  // Sets the min/max number inputs for current currency, converted from USD
  setCurrencyMinMaxInputs(currency, exrate = 1) {
    const [usdMin, usdMax] = this.basePriceRangeUSD;
    let min = usdMin,
      max = usdMax;
    if (currency !== "USD") {
      min = Math.round(usdMin * exrate);
      max = Math.round(usdMax * exrate);
    }
    if (this.hasPriceMinInputTarget) {
      this.priceMinInputTarget.value = min;
    }
    if (this.hasPriceMaxInputTarget) {
      this.priceMaxInputTarget.value = max;
    }
  }

  // Central logic to update min/max, slider, and histogram when currency changes
  updateCurrencyAndSliderRanges() {
    const currency = localStorage.getItem("selectedCurrency") || "USD";
    const exrate = parseFloat(localStorage.getItem("exchangeRate") || "1");
    this.setCurrencyMinMaxInputs(currency, exrate);
    this.initializeSlider();
    this.renderHistogram();
  }

  setInitialCurrencySymbol() {
    const currency = localStorage.getItem("selectedCurrency") || "USD";
    this.updateCurrencySymbol(currency);
  }

  // --- Stimulus Event Handlers ---

  handleCurrencyChangeEvent = (event) => {
    const currency = event?.detail?.currency || "USD";
    const exrate = parseFloat(localStorage.getItem("exchangeRate") || "1");
    this.initializeSlider();
    this.renderHistogram();
    this.updateCurrencySymbol(currency);
    this.setCurrencyMinMaxInputs(currency, exrate);
    this.updateMinMaxInputs();
    this.prevCurrency = currency;
  };

  updateMinMaxInputs() {
    this.priceMinInputTarget.attributes.min.value = this.allowedMin;
    this.priceMinInputTarget.attributes.max.value = this.allowedMax;
    this.priceMaxInputTarget.attributes.min.value = this.allowedMin;
    this.priceMaxInputTarget.attributes.max.value = this.allowedMax;
  }

  updateCurrencySymbol(currency) {
    if (!this.hasCurrencySymbolTarget) return;
    const symbol = getCurrencySymbol(currency);
    this.currencySymbolTargets.forEach((node) => {
      node.textContent = symbol;
    });
  }

  // --- Slider logic ---
  initializeSlider() {
    if (!this.hasRangeSliderTarget) return;
    const sliderEl = this.rangeSliderTarget;
    // If a slider instance exists, destroy it before re-creating
    if (sliderEl.noUiSlider) {
      sliderEl.noUiSlider.destroy();
    }

    // Use the current (possibly newly converted) min/max values
    const convertedRange = this.basePriceRangeUSD;
    const min = this.convertToActiveCurrency(
      parseInt(convertedRange[0], 10) || 0,
    );
    const max = this.convertToActiveCurrency(
      parseInt(convertedRange[1], 10) || 1000,
    );

    noUiSlider.create(sliderEl, {
      start: [min, max],
      connect: true,
      step: 1,
      tooltips: false,
      range: {
        min: this.allowedMin,
        max: this.allowedMax,
      },
      format: {
        to: (value) => Math.round(value),
        from: (value) => Math.round(value),
      },
    });

    this.slider = sliderEl.noUiSlider;

    // Slider => Inputs + hidden input (update in both currency and USD)
    this.slider.on("update", (values, handle) => {
      const minVal = parseInt(values[0], 10);
      const maxVal = parseInt(values[1], 10);
      if (this.priceMinInputTarget) this.priceMinInputTarget.value = minVal;
      if (this.priceMaxInputTarget) this.priceMaxInputTarget.value = maxVal;
      this.updatePriceRange();
      this.updateHistogramHighlight();
    });

    // Inputs => Slider (when typing manually)
    this.priceMinInputTarget.addEventListener("change", () => {
      this.slider.set([this.priceMinInputTarget.value, null]);
    });
    this.priceMaxInputTarget.addEventListener("change", () => {
      this.slider.set([null, this.priceMaxInputTarget.value]);
    });
  }

  // --- Price sync logic ---

  // Updates the hidden input as USD and updates histogram marker
  updatePriceRange() {
    const minConverted = parseInt(this.priceMinInputTarget.value, 10) || 0;
    const maxConverted = parseInt(this.priceMaxInputTarget.value, 10) || 0;
    const currency = localStorage.getItem("selectedCurrency") || "USD";
    let exrate = parseFloat(localStorage.getItem("exchangeRate") || "1");
    if (!exrate || exrate <= 0) exrate = 1;
    let minUSD = minConverted,
      maxUSD = maxConverted;
    if (currency !== "USD" && exrate) {
      minUSD = Math.round(minConverted / exrate);
      maxUSD = Math.round(maxConverted / exrate);
    }
    
    if (this.hasPriceRangeMinInputTarget && this.hasPriceRangeMaxInputTarget) {
      this.priceRangeMinInputTarget.value = minUSD;
      this.priceRangeMaxInputTarget.value = maxUSD;
    }
    
    // disable min/max input if value is default
    if (this.isDefaultPrice(`[${minUSD},${maxUSD}]`)){
      this.priceRangeMinInputTarget.disabled = true;
      this.priceRangeMaxInputTarget.disabled = true;
    } else {
      this.priceRangeMinInputTarget.disabled = false;
      this.priceRangeMaxInputTarget.disabled = false;
    }
    this.updateHistogramHighlight();
  }

  // --- UI, filter, and event logic ---

  open() {
    this.drawerTarget.closest(
      '[data-hotel-filter-drawer-target="fdrawer"]',
    ).style.display = "";
    document.body.style.overflow = "hidden";
  }

  close(event) {
    if (!event || event.target.closest("button")) {
      this.drawerTarget.closest(
        '[data-hotel-filter-drawer-target="fdrawer"]',
      ).style.display = "none";
      document.body.style.overflow = "";
      return;
    }
    if (event.target === this.overlayTarget) {
      this.drawerTarget.closest(
        '[data-hotel-filter-drawer-target="fdrawer"]',
      ).style.display = "none";
      document.body.style.overflow = "";
    }
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  clearAll(event) {
    event.preventDefault();
    const form = this.formTarget;
    if (form) {
      form.reset();
      form
        .querySelectorAll('input[type="checkbox"], input[type="radio"]')
        .forEach((el) => (el.checked = false));
    }
    this.resetPrice(); // reset price range

    // Reset amenities toggle buttons
    if (this.hasAmenitiesTarget) {
      const amenityBtns = this.amenitiesTarget.querySelectorAll("button[data-ids]");
      amenityBtns.forEach((btn) => {
        btn.dataset.selected = "false";
        btn.classList.remove("bg-black", "text-white");
        btn.classList.add("bg-gray-200", "text-black");
      });
    }

    // Reset star rating
    if (this.hasStarRatingInputTarget) {
      this.starRatingInputTarget.value = 0;
    }
    if (this.hasStarRatingTarget) {
      const stars = this.starRatingTarget.querySelectorAll("[data-star-value]");
      stars.forEach((star) => {
        const svg = star.querySelector("svg");
        if (svg) {
          svg.setAttribute("fill", "#F4F4F4");
        }
      });
    }
  }
  
  resetPrice() {
    if(this.hasPriceMinInputTarget) {
      this.priceMinInputTarget.value = this.allowedMin;
    }
    if(this.hasPriceMaxInputTarget) {
      this.priceMaxInputTarget.value = this.allowedMax;
    }
    this.slider.set([this.allowedMin, this.allowedMax]);
    this.renderHistogram();
  }

  // Checks if price_range is the default price (replace logic when there is a true default)
  isDefaultPrice(priceRangeStr) {
    // Placeholder logic, always returns false (customize as needed)
    const originalMinPrice = parseInt(
      this.priceMinInputTarget.getAttribute("min-usd"),
    );
    const originalMaxPrice = parseInt(
      this.priceMaxInputTarget.getAttribute("max-usd"),
    );
    const originalPriceRange = [originalMinPrice, originalMaxPrice];
    const currentPriceRange = JSON.parse(priceRangeStr);

    return (
      Array.isArray(currentPriceRange) &&
      originalPriceRange.length === currentPriceRange.length &&
      originalPriceRange.every((val, index) => val === currentPriceRange[index])
    );
  }

  toggleAmenity(event) {
    const btn = event.currentTarget;
    let selected = btn.dataset.selected === "true";
    selected = !selected;
    btn.dataset.selected = selected;

    if (selected) {
      btn.classList.add("bg-black", "text-white");
      btn.classList.remove("bg-gray-200", "text-black");
    } else {
      btn.classList.remove("bg-black", "text-white");
      btn.classList.add("bg-gray-200", "text-black");
    }

    // Remove any dynamically added amenities[] hidden inputs
    this.amenitiesInputTarget.querySelectorAll('input[name="amenities[]"]').forEach((input) => {
      input.remove();
    });

    // For all selected amenities, create a hidden input for each
    const allBtns = this.amenitiesTarget.querySelectorAll("button[data-ids]");
    let selectedNames = [];
    allBtns.forEach((b) => {
      if (b.dataset.selected === "true") {
        selectedNames.push(b.dataset.value);

        // Dynamically create a hidden input for this amenity
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "amenities[]";
        input.value = b.dataset.value;
        // Insert after the original amenitiesInputTarget
        this.amenitiesInputTarget.appendChild(input);
      }
    });

  }

  selectStar(event) {
    const selectedValue = parseInt(event.currentTarget.dataset.starValue, 10);
    const currentValue = parseInt(this.starRatingInputTarget.value, 10);
    const newValue = currentValue === selectedValue ? 0 : selectedValue;
    this.starRatingInputTarget.value = newValue;

    const stars = this.starRatingTarget.querySelectorAll("[data-star-value]");
    stars.forEach((star) => {
      const starVal = parseInt(star.dataset.starValue, 10);
      const svg = star.querySelector("svg");
      if (starVal <= newValue) {
        svg.setAttribute("fill", "#000000");
      } else {
        svg.setAttribute("fill", "#F4F4F4");
      }
    });
    
    // to disable sending star value as query parameter if the value if 0
    if (newValue === 0) {
      this.starRatingInputTarget.disabled = true;
    } else {
      this.starRatingInputTarget.disabled = false;
    }
  }

  // --- Histogram logic ---

  get allowedMin() {
    const minUSD = parseInt(
      this.priceMinInputTarget.getAttribute("min-usd") || "0",
      10,
    );
    const currency = localStorage.getItem("selectedCurrency") || "USD";
    let exrate = parseFloat(localStorage.getItem("exchangeRate") || "1");
    if (!exrate || exrate <= 0) exrate = 1;
    return exrate !== 1 ? Math.round(minUSD * exrate) : minUSD;
  }
  get allowedMax() {
    const maxUSD = parseInt(
      this.priceMaxInputTarget.getAttribute("max-usd") || "1000000",
      10,
    );
    const currency = localStorage.getItem("selectedCurrency") || "USD";
    let exrate = parseFloat(localStorage.getItem("exchangeRate") || "1");
    if (!exrate || exrate <= 0) exrate = 1;
    return exrate !== 1 ? Math.round(maxUSD * exrate) : maxUSD;
  }

  convertToActiveCurrency(number) {
    const currency = localStorage.getItem("selectedCurrency") || "USD";
    let exrate = parseFloat(localStorage.getItem("exchangeRate") || "1");
    if (!exrate || exrate <= 0) exrate = 1;
    return exrate !== 1 ? Math.round(number * exrate) : number;
  }

  renderHistogram() {
    if (!this.hasHistogramTarget) return;
    this.histogramBars = [];
    const binCount = 30;
    const minRange = this.allowedMin;
    const maxRange = this.allowedMax;
    const container = this.histogramTarget;
    container.innerHTML = "";
    this._histogramDistribution = Array.from(
      { length: binCount },
      () => Math.floor(Math.random() * 100) + 10,
    );
    const maxVal = Math.max(...this._histogramDistribution);
    for (let i = 0; i < binCount; i++) {
      const bar = document.createElement("div");
      bar.style.minWidth = "4px";
      bar.style.borderRadius = "9999px";
      const h = Math.floor(Math.random() * 100);
      bar.style.height = `${h}px`;
      bar.style.background = "#d1d5db";
      bar.className = "transition-colors duration-100";
      container.appendChild(bar);
      this.histogramBars.push(bar);
    }
    this.updateHistogramHighlight();
  }

  updateHistogramHighlight() {
    if (!this.histogramBars) return;
    const selMin = parseInt(
      this.priceMinInputTarget.value || this.allowedMin,
      10,
    );
    const selMax = parseInt(
      this.priceMaxInputTarget.value || this.allowedMax,
      10,
    );
    const minRange = this.allowedMin;
    const maxRange = this.allowedMax;
    const binCount = this.histogramBars.length;
    for (let i = 0; i < binCount; i++) {
      const binStart = minRange + ((maxRange - minRange) * i) / binCount;
      const binEnd = minRange + ((maxRange - minRange) * (i + 1)) / binCount;
      if (binEnd > selMin && binStart < selMax) {
        this.histogramBars[i].style.background = "#000";
      } else {
        this.histogramBars[i].style.background = "#d1d5db";
      }
    }
  }
}
